/**
 * FSP Licence Profile page — v31-1-10
 *
 * Route: /org/fsp-profile
 *
 * Features:
 * - View/create FSP licence profile for the organisation
 * - Edit FSP name, number, categories, sub-categories
 * - Multi-category selectors with canonical sub-category checkboxes
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { CAT1_SUB_CATEGORIES, CAT2_SUB_CATEGORIES } from '../../types/fspProfile';
import type { FspProfile, FspProfileCreateRequest } from '../../types/fspProfile';

// ---------------------------------------------------------------------------
// API helpers (inline, following existing org page patterns)
// ---------------------------------------------------------------------------

const fspApi = {
  getProfile: () => api.request<FspProfile>('/api/v1/orgs/fsp-profile'),
  createProfile: (body: FspProfileCreateRequest) =>
    api.request<FspProfile>('/api/v1/orgs/fsp-profile', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateProfile: (body: Partial<FspProfileCreateRequest>) =>
    api.request<FspProfile>('/api/v1/orgs/fsp-profile', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const FspProfilePage: React.FC = () => {
  const qc = useQueryClient();
  const { data: profile, isLoading, error } = useQuery<FspProfile>({
    queryKey: ['fsp-profile'],
    queryFn: fspApi.getProfile,
    retry: false,
  });

  const profileExists = !!profile?.id;

  const [fspName, setFspName] = useState('');
  const [fspNumber, setFspNumber] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [subCat1, setSubCat1] = useState<string[]>([]);
  const [subCat2, setSubCat2] = useState<string[]>([]);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (profile) {
      setFspName(profile.fsp_name);
      setFspNumber(profile.fsp_number);
      setCategories(profile.categories ?? []);
      setSubCat1(profile.sub_categories_cat1 ?? []);
      setSubCat2(profile.sub_categories_cat2 ?? []);
    }
  }, [profile]);

  const createMutation = useMutation({
    mutationFn: fspApi.createProfile,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fsp-profile'] });
      setEditing(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: fspApi.updateProfile,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fsp-profile'] });
      setEditing(false);
    },
  });

  const toggleCategory = (cat: string) => {
    setCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const toggleSubCat = (list: string[], setter: (v: string[]) => void, val: string) => {
    setter(list.includes(val) ? list.filter((v) => v !== val) : [...list, val]);
  };

  const handleSave = () => {
    const body: FspProfileCreateRequest = {
      fsp_name: fspName,
      fsp_number: fspNumber,
      categories,
      sub_categories_cat1: subCat1.length > 0 ? subCat1 : undefined,
      sub_categories_cat2: subCat2.length > 0 ? subCat2 : undefined,
    };

    if (profileExists) {
      updateMutation.mutate(body);
    } else {
      createMutation.mutate(body);
    }
  };

  const saving = createMutation.isPending || updateMutation.isPending;
  const saveError = createMutation.error || updateMutation.error;

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
      </div>
    );
  }

  const isNew = !profileExists;

  return (
    <div className="p-6 max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">FSP Licence Profile</h1>
        {profileExists && !editing && (
          <button
            onClick={() => setEditing(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            Edit
          </button>
        )}
      </div>

      {error && !isNew && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <p className="text-red-700 dark:text-red-300 text-sm">
            {(error as Error).message?.includes('404')
              ? 'No FSP profile exists yet. Create one below.'
              : (error as Error).message}
          </p>
        </div>
      )}

      {(isNew || editing) ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-6">
          {saveError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3">
              <p className="text-red-600 dark:text-red-400 text-sm">{(saveError as Error).message}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                FSP Name
              </label>
              <input
                type="text"
                value={fspName}
                onChange={(e) => setFspName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g. Marome Investments (Pty) Ltd"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                FSP Number
              </label>
              <input
                type="text"
                value={fspNumber}
                onChange={(e) => setFspNumber(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g. 12345"
              />
            </div>
          </div>

          {/* Categories */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Licence Categories
            </label>
            <div className="flex gap-4">
              {['I', 'II'].map((cat) => (
                <label key={cat} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={categories.includes(cat)}
                    onChange={() => toggleCategory(cat)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Category {cat}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Cat I sub-categories */}
          {categories.includes('I') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category I Sub-categories
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {CAT1_SUB_CATEGORIES.map((sc) => (
                  <label key={sc} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={subCat1.includes(sc)}
                      onChange={() => toggleSubCat(subCat1, setSubCat1, sc)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600"
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                      {sc.replace(/_/g, ' ')}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Cat II sub-categories */}
          {categories.includes('II') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category II Sub-categories
              </label>
              <div className="grid grid-cols-2 gap-2">
                {CAT2_SUB_CATEGORIES.map((sc) => (
                  <label key={sc} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={subCat2.includes(sc)}
                      onChange={() => toggleSubCat(subCat2, setSubCat2, sc)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600"
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                      {sc.replace(/_/g, ' ')}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving || !fspName || !fspNumber || categories.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {saving ? 'Saving...' : isNew ? 'Create Profile' : 'Save Changes'}
            </button>
            {editing && (
              <button
                onClick={() => {
                  setEditing(false);
                  if (profile) {
                    setFspName(profile.fsp_name);
                    setFspNumber(profile.fsp_number);
                    setCategories(profile.categories ?? []);
                    setSubCat1(profile.sub_categories_cat1 ?? []);
                    setSubCat2(profile.sub_categories_cat2 ?? []);
                  }
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      ) : (
        /* Read-only view */
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">FSP Name</p>
              <p className="text-gray-900 dark:text-white font-medium">{profile?.fsp_name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">FSP Number</p>
              <p className="text-gray-900 dark:text-white font-medium">{profile?.fsp_number}</p>
            </div>
          </div>

          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Categories</p>
            <div className="flex gap-2">
              {profile?.categories.map((c) => (
                <span
                  key={c}
                  className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded text-sm"
                >
                  Cat {c}
                </span>
              ))}
            </div>
          </div>

          {profile?.sub_categories_cat1 && profile.sub_categories_cat1.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                Cat I Sub-categories
              </p>
              <div className="flex flex-wrap gap-2">
                {profile.sub_categories_cat1.map((sc) => (
                  <span
                    key={sc}
                    className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-sm capitalize"
                  >
                    {sc.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {profile?.sub_categories_cat2 && profile.sub_categories_cat2.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                Cat II Sub-categories
              </p>
              <div className="flex flex-wrap gap-2">
                {profile.sub_categories_cat2.map((sc) => (
                  <span
                    key={sc}
                    className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-sm capitalize"
                  >
                    {sc.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {profile?.licence_verified_at && (
            <div className="text-sm text-green-600 dark:text-green-400">
              Verified on {new Date(profile.licence_verified_at).toLocaleDateString()}
              {profile.licence_verified_by && ` by ${profile.licence_verified_by}`}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FspProfilePage;
